import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TournamentsRepository, AttendeesRepository, CourtsRepository } from '../services/repository';
import { showSnackbar } from './Snackbar.jsx';
import PlayerAutocomplete from './PlayerAutocomplete.jsx';
import './TournamentCreator.css';

const TournamentCreator = () => {
  const navigate = useNavigate();
  const [eventDetails, setEventDetails] = useState({});
  const [attendees, setAttendees] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    numberOfPoints: 21,
    numberOfAttendees: 4,
    selectedCourts: [],
    type: 'americano'
  });
  const [participants, setParticipants] = useState([]);

  const minAttendees = 4;
  const maxAttendees = 40;

  const clampAttendeeCount = (count) => {
    if (count < minAttendees) return minAttendees;
    if (count > maxAttendees) return maxAttendees;
    return count;
  };

  // Helper function to extract first name from full name (format: "Last First")
  const getFirstName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return '';
    const nameParts = fullName.trim().split(' ');
    return nameParts[nameParts.length - 1] || fullName; // Return last part (first name) or full name if no spaces
  };

  // Helper function to get last name from full name (format: "Last First")
  const getLastName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return '';
    const nameParts = fullName.trim().split(' ');
    return nameParts.slice(0, -1).join(' ') || fullName; // Return all parts except last (last name) or full name if no spaces
  };

  // Helper function to resolve display name conflicts by adding letters from full name
  const resolveDisplayNameConflicts = (participants) => {
    const resolvedParticipants = [...participants];
    
    // Initialize all participants with just their first name
    resolvedParticipants.forEach((participant, index) => {
      if (participant.name.trim()) {
        const firstName = getFirstName(participant.name.trim());
        resolvedParticipants[index].displayName = firstName;
      }
    });
    
    // Keep track of which participants still need to be made unique
    let participantsToProcess = resolvedParticipants
      .map((participant, index) => ({ participant, index }))
      .filter(({ participant }) => participant.name.trim());
    
    let letterPosition = 1;
    
    // Continue until all participants have unique display names
    while (participantsToProcess.length > 0) {
      // Count occurrences of each display name
      const displayNameCounts = new Map();
      participantsToProcess.forEach(({ participant }) => {
        const count = displayNameCounts.get(participant.displayName) || 0;
        displayNameCounts.set(participant.displayName, count + 1);
      });
      
      // Find participants with non-unique display names
      const nonUniqueParticipants = participantsToProcess.filter(({ participant }) => 
        displayNameCounts.get(participant.displayName) > 1
      );
      
      // If all are unique, we're done
      if (nonUniqueParticipants.length === 0) {
        break;
      }
      
      // Add one more letter of last name to non-unique participants
      nonUniqueParticipants.forEach(({ participant, index }) => {
        const fullName = participant.name.trim();
        const firstName = getFirstName(fullName);
        const lastName = getLastName(fullName);
        
        if (letterPosition <= lastName.length) {
          const truncatedLastName = lastName.substring(0, letterPosition);
          resolvedParticipants[index].displayName = firstName + ' ' + truncatedLastName;
        } else {
          // If we've run out of letters, append a number
          let counter = 1;
          while (true) {
            const candidateName = firstName + ' ' + lastName + counter;
            // Check if this name is already used
            const isUsed = resolvedParticipants.some((p, i) => 
              i !== index && p.displayName === candidateName
            );
            if (!isUsed) {
              resolvedParticipants[index].displayName = candidateName;
              break;
            }
            counter++;
          }
        }
      });
      
      // Update the list of participants to process (remove those that are now unique)
      // We need to recalculate the counts after the changes
      const newDisplayNameCounts = new Map();
      resolvedParticipants.forEach((participant) => {
        if (participant.name.trim()) {
          const count = newDisplayNameCounts.get(participant.displayName) || 0;
          newDisplayNameCounts.set(participant.displayName, count + 1);
        }
      });
      
      participantsToProcess = resolvedParticipants
        .map((participant, index) => ({ participant, index }))
        .filter(({ participant }) => 
          participant.name.trim() && newDisplayNameCounts.get(participant.displayName) > 1
        );
      
      letterPosition++;
    }

    return resolvedParticipants;
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load event details from localStorage
      let details = {};
      try {
        details = JSON.parse(window.localStorage.getItem('eventDetails') || '{}');
      } catch (e) {
        details = {};
      }
      setEventDetails(details);

      // Load attendees if event ID exists
      let attendeesData = [];
      if (details.id) {
        attendeesData = await fetchAttendees(details.id);
        setAttendees(attendeesData);
      }
      // If no event ID, it's an empty tournament - no need to show error

      // Load courts
      const courtsData = await fetchCourts();
      setCourts(courtsData);

      // Determine tournament type based on event type
      let tournamentType = 'americano'; // default
      if (details.type) {
        // Map event type to tournament type
        switch (details.type.toLowerCase()) {
          case 'mexicano':
            tournamentType = 'mexicano';
            break;
          case 'americano':
            tournamentType = 'americano';
            break;
          default:
            tournamentType = 'americano'; // fallback
        }
      }

      // Set default form values
      const defaultAttendeeCount = clampAttendeeCount(details.maxAttendees || minAttendees);
      setFormData(prev => ({
        ...prev,
        name: details.name || '',
        numberOfPoints: 21,
        numberOfAttendees: defaultAttendeeCount,
        type: tournamentType
      }));

      // Initialize participants
      const newParticipants = initializeParticipants(attendeesData, defaultAttendeeCount);
      setParticipants(newParticipants);

    } catch (error) {
      console.error('Error loading initial data:', error);
      showSnackbar('Błąd ładowania danych');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendees = async (eventId) => {
    try {
              return await AttendeesRepository.fetchAttendees(eventId);
    } catch (e) {
      showSnackbar('Błąd ładowania uczestników: ' + (e.message || e));
      return [];
    }
  };

  const fetchCourts = async () => {
    try {
              return await CourtsRepository.fetchCourts();
    } catch (e) {
      showSnackbar('Błąd ładowanie kortów ' + (e.message || e));
      return [];
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Reinitialize participants when numberOfAttendees changes
    if (field === 'numberOfAttendees') {
      const newParticipants = initializeParticipants(attendees, value);
      setParticipants(newParticipants);
    }
  };

  const handleCourtToggle = (courtId) => {
    setFormData(prev => ({
      ...prev,
      selectedCourts: prev.selectedCourts.includes(courtId)
        ? prev.selectedCourts.filter(id => id !== courtId)
        : [...prev.selectedCourts, courtId]
    }));
  };

  const handleParticipantChange = (index, name, id) => {
    setParticipants(prev => {
      const newParticipants = [...prev];
      newParticipants[index] = { 
        ...newParticipants[index], 
        name: name,
        id: id,
        isAttendee: false,  // Mark as autocomplete-selected (not attendee)
        displayName: getFirstName(name)  // Set display name to first name by default
      };
      return newParticipants;
    });
  };

  const handleDisplayNameChange = (index, displayName) => {
    setParticipants(prev => {
      const newParticipants = [...prev];
      newParticipants[index] = { 
        ...newParticipants[index], 
        displayName: displayName
      };
      return newParticipants;
    });
  };

  // Function to check if a display name is duplicate
  const isDuplicateDisplayName = (index, displayName) => {
    if (!displayName || !displayName.trim()) return false;
    
    const currentDisplayName = displayName.trim().toLowerCase();
    return participants.some((participant, i) => 
      i !== index && 
      participant.name.trim() !== '' && 
      (participant.displayName?.trim() || participant.name.trim()).toLowerCase() === currentDisplayName
    );
  };

  // Function to check if there are any duplicate display names
  const hasDuplicateDisplayNames = () => {
    const validParticipants = participants.filter(p => p.name.trim() !== '');
    const displayNames = validParticipants.map(p => 
      (p.displayName?.trim() || p.name.trim()).toLowerCase()
    );
    return displayNames.some((name, index) => 
      displayNames.indexOf(name) !== index
    );
  };

  // Function to get participant display name with validation styling
  const getParticipantDisplayName = (participant) => {
    const displayName = participant.displayName?.trim() || participant.name.trim();
    const isDuplicate = isDuplicateDisplayName(
      participants.findIndex(p => p.id === participant.id && p.name === participant.name), 
      displayName
    );
    
    return {
      displayName,
      isDuplicate
    };
  };

  const handleRemoveParticipant = (index) => {
    setParticipants(prev => {
      const newParticipants = [...prev];
      newParticipants[index] = { name: '', id: undefined, isAttendee: false, displayName: '' };
      return newParticipants;
    });
  };

  const initializeParticipants = (attendees, numberOfAttendees) => {
    const newParticipants = [];
    
    // Fill with existing attendees first
    for (let i = 0; i < Math.min(attendees.length, numberOfAttendees); i++) {
      newParticipants.push({
        id: attendees[i].id,
        name: attendees[i].name,
        displayName: getFirstName(attendees[i].name),  // Set display name to first name by default
        isAttendee: true  // Mark as attendee
      });
    }
    
    // Fill remaining slots with empty participants
    for (let i = attendees.length; i < numberOfAttendees; i++) {
      newParticipants.push({
        id: undefined,
        name: '',
        displayName: '',
        isAttendee: false  // Mark as not attendee
      });
    }
    
    // Resolve display name conflicts by adding prefixes
    return resolveDisplayNameConflicts(newParticipants);
  };

  const handleStartTournament = async () => {
    // Validate form
    if (!formData.name.trim()) {
      showSnackbar('Wypełnij nazwę turnieju.');
      return;
    }

    // Validate participants
    const validParticipants = participants.filter(p => p.name.trim() !== '');
    
    if (validParticipants.length === 0) {
      showSnackbar('Dodaj przynajmniej jednego uczestnika.');
      return;
    }

    // Check for duplicate display names
    const displayNames = validParticipants.map(p => 
      (p.displayName?.trim() || p.name.trim()).toLowerCase()
    );
    const duplicateDisplayNames = displayNames.filter((name, index) => 
      displayNames.indexOf(name) !== index
    );

    if (duplicateDisplayNames.length > 0) {
      const uniqueDuplicates = [...new Set(duplicateDisplayNames)];
      showSnackbar(`Nazwy wyświetlane nie mogą się powtarzać: ${uniqueDuplicates.join(', ')}`);
      return;
    }

    try {
      const participantsData = validParticipants.map(participant => {
        // If participant is from attendees, use klubyId field
        // If participant is from autocomplete, use id field
        if (participant.isAttendee) {
          return {
            klubyId: participant.id,
            name: participant.name.trim(),
            displayName: participant.displayName?.trim() || participant.name.trim()
          };
        } else {
          return {
            id: participant.id,
            name: participant.name.trim(),
            displayName: participant.displayName?.trim() || participant.name.trim()
          };
        }
      });

      const selectedCourtIds = courts
        .filter(court => formData.selectedCourts.includes(court.id))
        .map(court => court.id);

      const requestBody = {
        name: formData.name.trim(),
        participants: participantsData,
        courtIds: selectedCourtIds,
        numberOfPoints: formData.numberOfPoints,
        type: formData.type
      };

              const data = await TournamentsRepository.createTournament(requestBody);
      navigate(`/tournament/${data.id}`);
    } catch (error) {
      showSnackbar('Błąd: ' + error.message);
    }
  };

  const renderAttendeesList = () => {
    if (participants.length === 0) {
      return <div className="text-center text-muted py-3">
        <i className="bi bi-people fs-4"></i>
        <p className="mt-2 mb-0">Brak uczestników</p>
      </div>;
    }

    return (
      <div className="participants-container">
        {participants.map((participant, index) => {
          const isLocked = participant.id !== null && participant.id !== undefined;
          
          return (
            <div key={index} className={`participant-item ${isLocked ? 'participant-locked-item' : 'participant-unlocked-item'}`}>
              <div className="participant-row">
                {isLocked ? (
                  // Locked participant - show name label and display name input
                  <div className="participant-locked">
                    <div className="participant-name-row">
                      <div className="participant-name-label">
                        <i className="bi bi-person-fill text-primary me-2"></i>
                        {participant.name}
                      </div>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        type="button"
                        onClick={() => handleRemoveParticipant(index)}
                        title="Usuń uczestnika"
                      >
                        ×
                      </button>
                    </div>
                    <div className="display-name-container">
                      <input
                        type="text"
                        className={`form-control display-name-input ${
                          isDuplicateDisplayName(index, participant.displayName) ? 'is-invalid' : ''
                        }`}
                        value={participant.displayName || ''}
                        onChange={(e) => handleDisplayNameChange(index, e.target.value)}
                        placeholder="Nazwa wyświetlana"
                        title="Nazwa wyświetlana w turnieju"
                      />
                      {isDuplicateDisplayName(index, participant.displayName) && (
                        <div className="invalid-feedback">
                          Ta nazwa wyświetlana jest już używana
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Unlocked participant - show as input only
                  <div className="participant-unlocked">
                    <div className="player-autocomplete">
                      <PlayerAutocomplete
                        value={participant.name}
                        onChange={(name, id) => handleParticipantChange(index, name, id)}
                        placeholder={`Uczestnik ${index + 1}`}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="participant-separator">
                <hr />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCourtsPicker = () => {
    return (
      <div className="row g-3">
        {courts.map(court => {
          const isSelected = formData.selectedCourts.includes(court.id);
          return (
            <div key={court.id} className="col-6 col-md-4 d-flex">
                              <div 
                  className={`card court-card flex-fill ${isSelected ? 'court-selected' : 'court-unselected'}`}
                  onClick={() => handleCourtToggle(court.id)}
                  style={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <div className="card-body p-3 text-center">
                    <div className="court-icon mb-2">
                      <i className={`bi ${isSelected ? 'bi-check-circle-fill text-primary' : 'bi-circle'} fs-4`}></i>
                    </div>
                    <h6 className="card-title mb-1">
                      {court.name}
                    </h6>
                    <small className="text-muted">
                      Kort {court.courtNumber || court.id}
                    </small>
                  </div>
                </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>;
  }

  return (
    <div className="card shadow-sm">
      <div className="card-body p-4">
        <h1 className="text-center mb-4">Tworzenie turnieju</h1>
        
        <div className="row">
          <div className="col-12 col-md-6 mb-3">
            <label htmlFor="eventName" className="form-label">Nazwa turnieju:</label>
            <input
              type="text"
              id="eventName"
              className="form-control"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Wprowadź nazwę turnieju"
            />
          </div>
         
        </div>
        <div className="row">
          <div className="col-12 col-md-6 mb-3">
          <div className="mb-3">
          <div className="d-flex align-items-center mb-2">
            <label htmlFor="tournamentType" className="form-label mb-0 me-2" style={{ minWidth: '90px' }}>Typ turnieju:</label>
            <select
              id="tournamentType"
      className="form-select form-select-sm"
      
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
            >
              <option value="americano">Americano</option>
              <option value="mexicano">Mexicano</option>
            </select>
            </div>
          </div>
            {/* Tournament settings */}
            <div className="mb-3">
              <div className="d-flex align-items-center mb-2">
                <label htmlFor="numberOfPoints" className="form-label mb-0 me-2" style={{ minWidth: '180px' }}>
                  Liczba punktów do wygranej:
                </label>
                <select
                  id="numberOfPoints"
                  className="form-select form-select-sm"
                  style={{ width: '80px' }}
                  value={formData.numberOfPoints}
                  onChange={(e) => handleInputChange('numberOfPoints', parseInt(e.target.value))}
                >
                  {[8, 16, 21, 24, 32, 40].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
              
              <div className="d-flex align-items-center mb-2">
                <label htmlFor="numberOfAttendees" className="form-label mb-0 me-2" style={{ minWidth: '180px' }}>
                  Liczba uczestników w turnieju:
                </label>
                <input
                  type="number"
                  id="numberOfAttendees"
                  className="form-control form-control-sm"
                  style={{ width: '80px' }}
                  min={minAttendees}
                  max={maxAttendees}
                  value={formData.numberOfAttendees}
                  onChange={(e) => handleInputChange('numberOfAttendees', clampAttendeeCount(parseInt(e.target.value, 10) || minAttendees))}
                />
              </div>
              
              <small className="form-text text-muted">
                Dostępnych uczestników: {attendees.length}
              </small>
            </div>
            
            {renderAttendeesList()}
          </div>

          <div className="col-12 col-md-6 mb-3">
            <label className="form-label">Wybierz korty:</label>
            {renderCourtsPicker()}
          </div>
        </div>

        <div className="d-grid gap-2 d-md-flex justify-content-md-end">
          <button
            className="btn btn-primary btn-lg"
            onClick={handleStartTournament}
            disabled={!formData.name.trim() || hasDuplicateDisplayNames()}
          >
            Rozpocznij turniej
          </button>
        </div>
      </div>
    </div>
  );
};

export default TournamentCreator; 